import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { Lock } from 'lucide-react'
import { useState } from 'react'
import { useSearchParams } from 'react-router-dom'
import { AccountingBridgeCard, type BridgeLine } from '@/components/AccountingBridgeCard'
import { AccountingSettingsCard, type AccountingSettings } from '@/components/AccountingSettingsCard'
import { AdvancesCard, type Advances, type Receivables } from '@/components/AdvancesCard'
import { BudgetForm, type BudgetItem } from '@/components/BudgetForm'
import { ChannelBreakdownTable, type ChannelFeeRow, type ChannelRevenueRow } from '@/components/ChannelBreakdownTable'
import { ClosedPeriodsPanel, type ClosedPeriod } from '@/components/ClosedPeriodsPanel'
import { DistributionTable, type Distribution, type DistributionShare, rub } from '@/components/DistributionTable'
import { ExpenseForm } from '@/components/ExpenseForm'
import { FeeBreakdownTable, type ProviderFeeRow } from '@/components/FeeBreakdownTable'
import { FeeRulesTable, type FeeRule, type FeeRuleDraft } from '@/components/FeeRulesTable'
import { LiabilitiesCard, type Liabilities } from '@/components/LiabilitiesCard'
import { ModelPnlTable, type ByModel } from '@/components/ModelPnlTable'
import { PlanFactTable, type PlanFact } from '@/components/PlanFactTable'
import { ReconciliationStatus, type Reconciliation } from '@/components/ReconciliationStatus'
import { FEE_KIND_LABEL, FEE_PROVIDER_LABEL } from '@/components/FeeLabels'
import { Button } from '@/components/ui/Button'
import { Card, StatCard } from '@/components/ui/Card'
import { LINK_CLASS } from '@/components/ui/Code'
import { useConfirm } from '@/components/ui/Confirm'
import { DataTable, QueryTable, type DataColumn } from '@/components/ui/DataTable'
import { DateRange, type Range } from '@/components/ui/DateRange'
import { EmptyState } from '@/components/ui/EmptyState'
import { ErrorState } from '@/components/ui/ErrorState'
import { Input, Label } from '@/components/ui/Input'
import { PageHeader } from '@/components/ui/PageHeader'
import { Pager } from '@/components/ui/Pager'
import { RowMenu } from '@/components/ui/RowMenu'
import { Tabs } from '@/components/ui/Tabs'
import { QueryState } from '@/components/ui/QueryState'
import { SectionHeader } from '@/components/ui/SectionHeader'
import { Segmented } from '@/components/ui/Segmented'
import { Select } from '@/components/ui/Select'
import { Skeleton, StatSkeleton } from '@/components/ui/Skeleton'
import { StatusBadge } from '@/components/ui/StatusBadge'
import { useToast } from '@/components/ui/Toast'
import { allCampaignsQuery } from '@/lib/acqQueries'
import type { AcqChannelsResponse } from '@/lib/acquisition'
import { byModelCsvPath, pnlCsvName, pnlCsvPath } from '@/lib/accountingCsv'
import { api, ApiError } from '@/lib/api'
import { canDo, useAuth } from '@/lib/auth'
import { saveBlob } from '@/lib/download'
import { expenseMeta, expenseSource, stornoState, type ExpenseRow, type ExpensesResponse } from '@/lib/expenses'
import { BOLT, fmtDate, fmtInt, fmtMoney, fmtPct, fmtRate, fmtUsdt } from '@/lib/format'
import { moscowMonthStart, moscowToday } from '@/lib/msk'
import { subDecimal, sumDecimal } from '@/lib/num'
import { CHANNELS, channelLabel } from '@/lib/paymentLabels'

/** P&L months of the chosen period as CSV. api.blob: the bearer token and its silent refresh
 *  (a plain fetch failed with 401 once the access token expired). */
async function downloadCsv(range: Range): Promise<void> {
  saveBlob(await api.blob(pnlCsvPath(range.from, range.to)), pnlCsvName(range.from, range.to))
}

type Tab = 'summary' | 'models' | 'planfact' | 'fees' | 'expenses' | 'partners' | 'distributions' | 'periods'

const TABS: { id: Tab; label: string }[] = [
  { id: 'summary', label: 'Сводка' },
  { id: 'models', label: 'По моделям' },
  { id: 'planfact', label: 'План-факт' },
  { id: 'fees', label: 'Комиссии' },
  { id: 'expenses', label: 'Расходы' },
  { id: 'partners', label: 'Участники и доли' },
  { id: 'distributions', label: 'Распределения' },
  { id: 'periods', label: 'Закрытие периодов' },
]

const CATEGORY_LABEL: Record<string, string> = {
  hosting: 'Хостинг и инфраструктура',
  marketing: 'Маркетинг',
  providers: 'Пополнение провайдеров',
  salary: 'Оплата труда',
  tax: 'Налоги и сборы',
  other: 'Прочее',
}

/** Periods are Moscow days whatever the browser's time zone (owner 2026-10-22). */
function monthStart(): string {
  return moscowMonthStart()
}
function todayIso(): string {
  return moscowToday()
}

export function Accounting() {
  const { role } = useAuth()
  const [params] = useSearchParams()
  // Deep link: /accounting?tab=expenses (e.g. from «Привлечение»: marketing spend is entered here).
  const [tab, setTab] = useState<Tab>(() => TABS.find((t) => t.id === params.get('tab'))?.id ?? 'summary')
  const canShares = canDo(role, 'accounting.shares')
  const canExpenses = canDo(role, 'accounting.expenses')
  const canFees = canDo(role, 'accounting.fees')
  return (
    <div>
      <PageHeader
        title="Бухгалтерия"
        description="Прибыль периода, комиссии, расходы и доли участников. Участники, доли и распределения меняет только владелец; расходы и тарифы комиссий вносят владелец, админ и финансист. Каждое действие в аудите."
        details={`Учёт по начислению, периоды — календарные дни по Москве. Прибыль = признанная выручка (доступ по подпискам и потраченные ${BOLT} по средней цене пользователя, минус сторно возвратов) − комиссии платёжных провайдеров − себестоимость генераций (по курсу на день генерации) − себестоимость неудачных генераций (оценка, отключается в настройках) − партнёрский расход (начислено − отменено − удержанная комиссия вывода) − комиссия сети за выплаты − расходы ± курсовые разницы по партнёрским USDT. Поступления оплат — не выручка: неизрасходованные ${BOLT} остаются обязательством. Ручное списание ${BOLT} админом — корректировка, а не выручка. Пополнение провайдера — аванс, а не расход.`}
        tabs={<Tabs label="Разделы бухгалтерии" idPrefix="accounting" panelId="accounting-panel" items={TABS} value={tab} onChange={setTab} />}
      />
      <div role="tabpanel" id="accounting-panel" aria-labelledby={`accounting-tab-${tab}`}>
        {tab === 'summary' && <SummaryTab canRun={canExpenses} canSettings={canShares} />}
        {tab === 'models' && <ModelsTab />}
        {tab === 'planfact' && <PlanFactTab canEdit={canExpenses} />}
        {tab === 'fees' && <FeesTab canEdit={canFees} />}
        {tab === 'expenses' && <ExpensesTab canEdit={canExpenses} />}
        {tab === 'partners' && <PartnersTab canEdit={canShares} />}
        {tab === 'distributions' && <DistributionsTab canEdit={canShares} />}
        {tab === 'periods' && <PeriodsTab />}
      </div>
    </div>
  )
}

// ───────────────────────────── summary ─────────────────────────────

interface Summary {
  period_start: string
  period_end: string
  /** paid payments of the period by currency (cash, not revenue) */
  revenue_by_currency: Record<string, string>
  /** RECOGNIZED revenue = access + consumption + reversals (docs/accounting.md) */
  revenue_rub: string
  revenue_access_rub?: string
  revenue_consumption_rub?: string
  /** negative: what refunds took back from revenue already recognized */
  revenue_reversals_rub?: string
  /** what paid payments brought in the period */
  cash_in_rub?: string
  payments_count: number
  provider_cost_usd: string
  provider_cost_rub: string
  generations: number
  /** completed payouts, sent (reference; the P&L uses referral_expense_rub) */
  referral_payouts_usdt: string
  referral_payouts_rub: string
  referral_accrued_rub?: string
  referral_reversed_rub?: string
  referral_fee_retained_rub?: string
  /** accrued − cancelled − withheld payout fee: the partner line of the P&L */
  referral_expense_rub?: string
  payment_fees_rub: string
  payout_fees_rub: string
  fees_by_provider: ProviderFeeRow[]
  /** per payment channel (method + issuing gateway), paid payments of the period */
  revenue_by_channel?: ChannelRevenueRow[]
  /** per payment channel, with fees; narrowed by `?channel=` (the P&L lines stay whole) */
  fees_by_channel?: ChannelFeeRow[]
  channel?: string | null
  payout_fees_sources: Record<string, string>
  expenses_rub: string
  expenses_by_category: Record<string, string>
  profit_rub: string
  /** null («—») while the recognized revenue is below 1 ₽ */
  margin_pct: string | null
  rates: { usdt_rub: string; star_rub: string; live: boolean }
  /** payments refunded in the period (by the refund date) — cash_in keeps them */
  refunds_rub?: string
  refunds_count?: number
  conversions_rub?: string
  liability_change_rub?: string
  bridge_other_rub?: string
  /** cash in → recognized revenue */
  bridge?: BridgeLine[]
  /** rule estimates of fees on refunded payments, by channel («оценка») */
  refunded_fee_estimates?: Record<string, string>
  /** admin debits: value written off without revenue (≤ 0) */
  adjustments_rub?: string
  /** ratable subscription access recognized in the period (inside revenue_access_rub) */
  revenue_ratable_rub?: string
  deferred_change_rub?: string
  /** realized FX differences on partner USDT: gain > 0, loss < 0 */
  fx_diff_rub?: string
  /** estimated provider cost of failed generations («оценка») */
  failed_jobs_cost_rub?: string
  failed_jobs_cost_usd?: string
  failed_jobs?: number
  /** a closed month: the snapshot stored at closing */
  closed?: boolean
  /** a closed month: the profit the live data gives now (late fee facts) */
  live_profit_rub?: string
  /** a custom range: the closed months it touches (computed live) */
  closed_months?: string[]
  /** GET /summary only: a snapshot as of now */
  liabilities?: Liabilities
  advances?: Advances
  receivables?: Receivables
  reconciliation?: Reconciliation | null
}

const MONTH_NAMES = ['январь', 'февраль', 'март', 'апрель', 'май', 'июнь', 'июль', 'август', 'сентябрь', 'октябрь', 'ноябрь', 'декабрь']

/** «2026-09-01» → «Сентябрь 2026». */
function monthName(periodStart: string): string {
  const [y, m] = periodStart.split('-')
  const name = MONTH_NAMES[Number(m) - 1]
  return name ? `${name[0]?.toUpperCase()}${name.slice(1)} ${y}` : periodStart.slice(0, 7)
}

const MONTH_COLUMNS: DataColumn<Summary>[] = [
  {
    id: 'month',
    header: 'Месяц',
    cell: (m) => (
      <span className="inline-flex items-center gap-2 whitespace-nowrap text-ink-1">
        {monthName(m.period_start)}
        {m.closed && (
          <StatusBadge tone="neutral" icon={<Lock />}>
            Закрыт
          </StatusBadge>
        )}
      </span>
    ),
    mobile: 'title',
  },
  { id: 'revenue', header: 'Выручка', type: 'money', unit: '₽', cell: (m) => fmtMoney(m.revenue_rub), mobile: 'meta' },
  { id: 'cash', header: 'Поступления', type: 'money', unit: '₽', className: 'text-ink-2', cell: (m) => fmtMoney(m.cash_in_rub), mobile: 'hidden' },
  {
    id: 'fees',
    header: 'Комиссии',
    type: 'money',
    unit: '₽',
    className: 'text-ink-2',
    cell: (m) => fmtMoney(sumDecimal([m.payment_fees_rub ?? '0', m.payout_fees_rub ?? '0'])),
    mobile: 'hidden',
  },
  { id: 'refunds', header: 'Возвраты', type: 'money', unit: '₽', className: 'text-ink-2', cell: (m) => fmtMoney(m.refunds_rub ?? '0'), mobile: 'hidden' },
  { id: 'providers', header: 'Провайдеры', type: 'money', unit: '₽', className: 'text-ink-2', cell: (m) => fmtMoney(m.provider_cost_rub), mobile: 'hidden' },
  { id: 'referral', header: 'Партнёрка', type: 'money', unit: '₽', className: 'text-ink-2', cell: (m) => fmtMoney(m.referral_expense_rub ?? m.referral_payouts_rub), mobile: 'hidden' },
  { id: 'expenses', header: 'Расходы', type: 'money', unit: '₽', className: 'text-ink-2', cell: (m) => fmtMoney(m.expenses_rub), mobile: 'hidden' },
  { id: 'profit', header: 'Прибыль', type: 'money', unit: '₽', cell: (m) => fmtMoney(m.profit_rub), mobile: 'amount' },
  { id: 'margin', header: 'Маржа', type: 'percent', className: 'text-ink-2', cell: (m) => marginText(m.margin_pct), mobile: 'meta' },
]

/** The word next to the FX number: a sign alone is not a state (rule 21). */
function fxHint(v: string | undefined): string {
  const n = Number(v ?? '0')
  if (n > 0) return 'доход: выплаты и переводы партнёрам по курсу ниже начисления'
  if (n < 0) return 'расход: выплаты и переводы партнёрам по курсу выше начисления'
  return 'партнёрские USDT: курс выплат совпал с курсом начисления'
}

/** «—» while the revenue is below 1 ₽ (a margin of −309 437 % told nothing). */
function marginText(v: string | null | undefined): string {
  return v === null || v === undefined ? '—' : fmtPct(v)
}

function SummaryTab({ canRun, canSettings }: { canRun: boolean; canSettings: boolean }) {
  const toast = useToast()
  const qc = useQueryClient()
  const [range, setRange] = useState<Range>({ from: monthStart(), to: todayIso() })
  const csv = useMutation({
    mutationFn: () => downloadCsv(range),
    onError: () => toast.error('Не удалось скачать CSV'),
  })
  const q = `?date_from=${range.from}&date_to=${range.to}`
  const summaryQ = useQuery({
    queryKey: ['acc-summary', range],
    queryFn: () => api.get<Summary>(`/accounting/summary${q}`),
  })
  const monthly = useQuery({
    queryKey: ['acc-monthly'],
    queryFn: () => api.get<{ items: Summary[] }>('/accounting/monthly?months=12'),
  })
  const settingsQ = useQuery({
    queryKey: ['acc-settings'],
    queryFn: () => api.get<AccountingSettings>('/accounting/settings'),
  })
  const saveSettings = useMutation({
    mutationFn: (patch: Partial<AccountingSettings>) => api.patch<AccountingSettings>('/accounting/settings', patch),
    onSuccess: (r) => {
      qc.setQueryData(['acc-settings'], r)
      void qc.invalidateQueries({ queryKey: ['acc-summary'] })
      toast.success('Настройки учёта сохранены')
    },
  })
  const run = useMutation({
    mutationFn: () => api.post<Reconciliation>('/accounting/reconciliation/run'),
    onSuccess: (r) => {
      if (r.ok) toast.success('Сверено: расхождений нет')
      else toast.error('Сверка нашла расхождение')
      qc.invalidateQueries({ queryKey: ['acc-summary'] })
    },
    onError: (e) => toast.error(e instanceof ApiError ? e.message : 'Не удалось сверить'),
  })
  return (
    <div className="flex flex-col gap-4">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <DateRange value={range} onChange={setRange} presets={[7, 30, 90, 365]} />
        <Button size="sm" variant="secondary" loading={csv.isPending} onClick={() => csv.mutate()}>
          Скачать CSV по месяцам периода
        </Button>
      </div>
      <QueryState
        query={summaryQ}
        skeleton={
          <div className="flex flex-col gap-4">
            <StatSkeleton count={3} className="xl:grid-cols-3" />
            <StatSkeleton count={4} className="xl:grid-cols-4" />
            <Skeleton className="h-32 w-full" />
          </div>
        }
        errorTitle="Не удалось загрузить сводку"
        framed
      >
        {(data) => (
          <>
            {data.closed && (
              <Card>
                <div className="flex flex-wrap items-center gap-2 text-body text-ink-2">
                  <StatusBadge tone="neutral" icon={<Lock />}>
                    Месяц закрыт
                  </StatusBadge>
                  Цифры — снимок на момент закрытия.
                  {data.live_profit_rub !== undefined && data.live_profit_rub !== data.profit_rub && (
                    <span className="tnum">По живым данным прибыль сейчас {fmtMoney(data.live_profit_rub, 'RUB')}.</span>
                  )}
                </div>
              </Card>
            )}
            {!data.closed && (data.closed_months?.length ?? 0) > 0 && (
              <Card>
                <div className="flex flex-wrap items-center gap-2 text-body text-ink-2">
                  <StatusBadge tone="warn" icon={<Lock />}>
                    Включает закрытые месяцы
                  </StatusBadge>
                  Период посчитан по живым данным; отчёт закрытого месяца — снимок, выберите месяц целиком.
                </div>
              </Card>
            )}
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-3">
              <StatCard label="Выручка, ₽" value={fmtMoney(data.revenue_rub)} hint={`признана: доступ по подпискам и потраченные ${BOLT}`} />
              <StatCard
                label="Поступления оплат, ₽"
                value={fmtMoney(data.cash_in_rub)}
                hint={`оплачено за период, возвраты отдельно: ${fmtMoney(data.refunds_rub ?? '0', 'RUB')}`}
              />
              <StatCard label="Прибыль, ₽" value={fmtMoney(data.profit_rub)} hint={`маржа ${marginText(data.margin_pct)}`} />
            </div>
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-4">
              <StatCard
                label="Комиссии, ₽"
                value={fmtMoney(sumDecimal([data.payment_fees_rub, data.payout_fees_rub]))}
              />
              <StatCard label="Себестоимость генераций, ₽" value={fmtMoney(data.provider_cost_rub)} hint="по курсу на день генерации" />
              <StatCard label="Партнёрка, ₽" value={fmtMoney(data.referral_expense_rub ?? data.referral_payouts_rub)} hint="начислено за период" />
              <StatCard label="Расходы, ₽" value={fmtMoney(data.expenses_rub)} />
            </div>
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-3">
              <StatCard
                label="Курсовые разницы, ₽"
                value={fmtMoney(data.fx_diff_rub ?? '0')}
                hint={fxHint(data.fx_diff_rub)}
              />
              <StatCard
                label="Себестоимость неудачных генераций (оценка), ₽"
                value={fmtMoney(data.failed_jobs_cost_rub ?? '0')}
                hint={`${fmtInt(data.failed_jobs ?? 0)} задач, по котировке`}
              />
              <StatCard
                label="Корректировки оператора, ₽"
                value={fmtMoney(data.adjustments_rub ?? '0')}
                hint={`ручные списания ${BOLT}, не выручка`}
              />
            </div>
            <Card>
              <div className="grid grid-cols-1 gap-x-6 gap-y-2 text-body sm:grid-cols-2 xl:grid-cols-3">
                <KV k="Платежей" v={fmtInt(data.payments_count)} />
                <KV k="Возвратов" v={fmtInt(data.refunds_count ?? 0)} />
                <KV k="Генераций" v={fmtInt(data.generations)} />
                <KV k="Маржа" v={marginText(data.margin_pct)} />
                <KV k="Выручка: доступ по подпискам" v={fmtMoney(data.revenue_access_rub, 'RUB')} />
                <KV k={`Выручка: потраченные ${BOLT}`} v={fmtMoney(data.revenue_consumption_rub, 'RUB')} />
                <KV k="Выручка: сторно возвратов" v={fmtMoney(data.revenue_reversals_rub, 'RUB')} />
                {Number(data.revenue_ratable_rub ?? '0') !== 0 && (
                  <KV k="в том числе доступ по дням периода" v={fmtMoney(data.revenue_ratable_rub, 'RUB')} />
                )}
                {Number(data.deferred_change_rub ?? '0') !== 0 && (
                  <KV k="Прирост отложенной выручки по подпискам" v={fmtMoney(data.deferred_change_rub, 'RUB')} />
                )}
                <KV k="Возвраты (по дате возврата)" v={fmtMoney(data.refunds_rub ?? '0', 'RUB')} />
                {Object.entries(data.revenue_by_currency).map(([c, v]) => (
                  <KV key={c} k={`Поступления ${c}`} v={fmtMoney(v, c)} />
                ))}
                {(data.revenue_by_channel ?? []).map((r) => (
                  <KV
                    key={r.channel}
                    k={`Поступления: ${r.label || channelLabel(r.channel)}`}
                    v={fmtMoney(r.gross_rub, 'RUB')}
                  />
                ))}
                <KV k="Себестоимость, $" v={fmtMoney(data.provider_cost_usd)} />
                <KV k="Комиссии оплат, ₽" v={fmtMoney(data.payment_fees_rub)} />
                <KV k="Партнёрка: начислено" v={fmtMoney(data.referral_accrued_rub, 'RUB')} />
                <KV k="Партнёрка: отменено" v={fmtMoney(data.referral_reversed_rub, 'RUB')} />
                <KV k="Партнёрка: удержанная комиссия вывода" v={fmtMoney(data.referral_fee_retained_rub, 'RUB')} />
                <KV k="Выплачено партнёрам" v={fmtMoney(data.referral_payouts_usdt, 'USDT')} />
                <KV k="Комиссия сети за выплаты, ₽" v={fmtMoney(data.payout_fees_rub)} />
                <KV k="Курс USDT/RUB" v={`${fmtRate(data.rates.usdt_rub)}${data.rates.live ? '' : ' (фикс.)'}`} />
                <KV k="1 Star" v={fmtRate(data.rates.star_rub)} />
                {Object.entries(data.expenses_by_category).map(([c, v]) => (
                  // expenses_by_category sums amount_rub: the unit goes with the value (§9)
                  <KV key={c} k={`Расходы: ${CATEGORY_LABEL[c] ?? c}`} v={fmtMoney(v, 'RUB')} />
                ))}
              </div>
            </Card>
            {data.bridge && <AccountingBridgeCard lines={data.bridge} />}
            <LiabilitiesCard data={data.liabilities} />
            <AdvancesCard advances={data.advances} receivables={data.receivables} />
            <ReconciliationStatus
              data={data.reconciliation ?? null}
              canRun={canRun}
              running={run.isPending}
              onRun={() => run.mutate()}
            />
          </>
        )}
      </QueryState>
      <AccountingSettingsCard
        data={settingsQ.data ? { ...settingsQ.data, can_edit: settingsQ.data.can_edit && canSettings } : undefined}
        busy={saveSettings.isPending}
        error={saveSettings.error instanceof ApiError ? saveSettings.error.message : saveSettings.isError ? 'Не удалось сохранить' : null}
        onChange={(patch) => saveSettings.mutate(patch)}
      />
      <section>
        <SectionHeader as="h3" title="По месяцам" description="Последние 12 месяцев по Москве, суммы в рублях; закрытые месяцы — снимок" />
        <QueryTable
          label="P&L по месяцам"
          query={monthly}
          rows={(d) => d.items}
          columns={MONTH_COLUMNS}
          getRowId={(m) => m.period_start}
          errorTitle="Не удалось загрузить помесячную таблицу"
          empty={<EmptyState title="Месяцев с данными пока нет" text="Первая строка появится после первой оплаты." />}
        />
      </section>
    </div>
  )
}

// ───────────────────────────── plan-fact ─────────────────────────────

type PlanSpan = 'month' | 'quarter'

/** «2026-10» → «2026-Q4». */
function quarterOf(month: string): string {
  const [y, m] = month.split('-')
  return `${y}-Q${Math.floor((Number(m) - 1) / 3) + 1}`
}

function PlanFactTab({ canEdit }: { canEdit: boolean }) {
  const toast = useToast()
  const qc = useQueryClient()
  const [span, setSpan] = useState<PlanSpan>('month')
  const [month, setMonth] = useState(() => monthStart().slice(0, 7))
  const period = span === 'month' ? month : quarterOf(month)
  const planFact = useQuery({
    queryKey: ['acc-plan-fact', period],
    queryFn: () => api.get<PlanFact>(`/accounting/plan-fact?period=${encodeURIComponent(period)}`),
  })
  const budgets = useQuery({
    queryKey: ['acc-budgets', month],
    queryFn: () => api.get<{ month: string; label: string; items: BudgetItem[] }>(`/accounting/budgets?month=${month}`),
  })
  const save = useMutation({
    mutationFn: (items: { category: string; amount_rub: string }[]) =>
      api.put<{ month: string; label: string; items: BudgetItem[] }>(`/accounting/budgets/${month}`, { items }),
    onSuccess: (r) => {
      qc.setQueryData(['acc-budgets', month], r)
      void qc.invalidateQueries({ queryKey: ['acc-plan-fact'] })
      toast.success('Бюджет сохранён')
    },
  })
  const csv = useMutation({
    mutationFn: async () =>
      saveBlob(await api.blob(`/accounting/plan-fact.csv?period=${encodeURIComponent(period)}`), `aivy-plan-fact-${period}.csv`),
    onError: () => toast.error('Не удалось скачать CSV'),
  })
  return (
    <div className="flex flex-col gap-4">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div className="flex flex-wrap items-end gap-3">
          <Segmented
            label="Период"
            value={span}
            options={[
              { value: 'month', label: 'Месяц' },
              { value: 'quarter', label: 'Квартал' },
            ]}
            onChange={setSpan}
          />
          <div>
            <Label htmlFor="planfact-month">{span === 'month' ? 'Месяц' : 'Месяц квартала'}</Label>
            <Input id="planfact-month" type="month" value={month} onChange={(e) => e.target.value && setMonth(e.target.value)} />
          </div>
        </div>
        <Button size="sm" variant="secondary" loading={csv.isPending} onClick={() => csv.mutate()}>
          Скачать CSV план-факта
        </Button>
      </div>
      <SectionHeader
        as="h3"
        title={planFact.data ? `План-факт: ${planFact.data.label}` : 'План-факт'}
        description="План — сумма бюджетов месяцев периода, факт — те же цифры, что в отчёте (закрытый месяц — снимок). Отклонение = факт − план."
      />
      <PlanFactTable data={planFact.data} error={planFact.error} onRetry={() => void planFact.refetch()} />
      <SectionHeader as="h3" title="Бюджет месяца" description="Расходы по статьям и цель по выручке (признанная выручка)." />
      {budgets.data ? (
        <BudgetForm
          monthLabel={budgets.data.label}
          items={budgets.data.items}
          canEdit={canEdit}
          busy={save.isPending}
          error={save.error instanceof ApiError ? save.error.message : save.isError ? 'Не удалось сохранить' : null}
          onSave={(items) => save.mutate(items)}
        />
      ) : budgets.error ? (
        <ErrorState title="Не удалось загрузить бюджет" error={budgets.error} onRetry={() => void budgets.refetch()} />
      ) : (
        <Skeleton className="h-32 w-full" />
      )}
    </div>
  )
}

// ───────────────────────────── by model ─────────────────────────────

function ModelsTab() {
  const toast = useToast()
  const [range, setRange] = useState<Range>({ from: monthStart(), to: todayIso() })
  const q = `?date_from=${range.from}&date_to=${range.to}`
  const byModel = useQuery({
    queryKey: ['acc-by-model', range],
    queryFn: () => api.get<ByModel>(`/accounting/by-model${q}`),
  })
  const csv = useMutation({
    mutationFn: async () =>
      saveBlob(await api.blob(byModelCsvPath(range.from, range.to)), `aivy-by-model-${range.from}-${range.to}.csv`),
    onError: () => toast.error('Не удалось скачать CSV'),
  })
  return (
    <div className="flex flex-col gap-4">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <DateRange value={range} onChange={setRange} presets={[7, 30, 90, 365]} />
        <Button size="sm" variant="secondary" loading={csv.isPending} onClick={() => csv.mutate()}>
          Скачать CSV
        </Button>
      </div>
      <ModelPnlTable data={byModel.data} error={byModel.error} onRetry={() => void byModel.refetch()} />
    </div>
  )
}

// ───────────────────────────── closed periods ─────────────────────────────

function PeriodsTab() {
  const qc = useQueryClient()
  const toast = useToast()
  const confirm = useConfirm()
  const periodsQ = useQuery({
    queryKey: ['acc-periods'],
    queryFn: () => api.get<{ items: ClosedPeriod[]; can_edit: boolean; current_month: string }>('/accounting/periods'),
  })
  const invalidate = () => {
    for (const key of ['acc-periods', 'acc-summary', 'acc-monthly']) qc.invalidateQueries({ queryKey: [key] })
  }
  const close = useMutation({
    mutationFn: ({ month, note }: { month: string; note: string }) => api.post(`/accounting/periods/${month}/close`, { note }),
    onSuccess: () => {
      toast.success('Месяц закрыт')
      invalidate()
    },
    onError: (e) => toast.error(e instanceof ApiError ? e.message : 'Не удалось закрыть'),
  })
  const reopen = useMutation({
    mutationFn: (month: string) => api.post(`/accounting/periods/${month.slice(0, 7)}/reopen`),
    onSuccess: () => {
      toast.success('Месяц открыт заново')
      invalidate()
    },
    onError: (e) => toast.error(e instanceof ApiError ? e.message : 'Не удалось открыть'),
  })
  if (periodsQ.error && !periodsQ.data) {
    return (
      <Card>
        <ErrorState title="Не удалось загрузить периоды" error={periodsQ.error} retrying={periodsQ.isFetching} onRetry={() => void periodsQ.refetch()} />
      </Card>
    )
  }
  return (
    <ClosedPeriodsPanel
      items={periodsQ.data?.items}
      currentMonth={periodsQ.data?.current_month ?? monthStart()}
      canEdit={periodsQ.data?.can_edit ?? false}
      busy={close.isPending || reopen.isPending}
      onClose={async (month, note) => {
        if (
          await confirm({
            title: `Закрыть ${monthName(`${month}-01`).toLowerCase()}?`,
            body: 'Отчёт месяца замёрзнет, расходы, пополнения, тарифы и распределения с датой в нём перестанут приниматься. Открыть заново может владелец.',
            confirmLabel: 'Закрыть месяц',
          })
        )
          close.mutate({ month, note })
      }}
      onReopen={async (p) => {
        if (
          await confirm({
            title: `Открыть ${p.label} заново?`,
            body: 'Снимок отчёта будет удалён (он останется в журнале аудита), месяц снова начнёт меняться.',
            confirmLabel: 'Открыть заново',
            danger: true,
          })
        )
          reopen.mutate(p.month)
      }}
    />
  )
}

function KV({ k, v }: { k: string; v: string }) {
  return (
    <div className="flex justify-between gap-3">
      <span className="min-w-0 text-ink-2">{k}</span>
      <span className="tnum shrink-0 text-right text-ink-1">{v}</span>
    </div>
  )
}

// ───────────────────────────── fees ─────────────────────────────

interface RulesResponse {
  items: FeeRule[]
  kinds: Record<string, string[]>
  currencies: string[]
  can_edit: boolean
}

function FeesTab({ canEdit }: { canEdit: boolean }) {
  const qc = useQueryClient()
  const toast = useToast()
  const confirm = useConfirm()
  const [range, setRange] = useState<Range>({ from: monthStart(), to: todayIso() })
  const [channel, setChannel] = useState('')
  const q = `?date_from=${range.from}&date_to=${range.to}${channel ? `&channel=${encodeURIComponent(channel)}` : ''}`
  const summary = useQuery({
    queryKey: ['acc-summary', range, channel],
    queryFn: () => api.get<Summary>(`/accounting/summary${q}`),
  })
  const rules = useQuery({
    queryKey: ['acc-fee-rules'],
    queryFn: () => api.get<RulesResponse>('/accounting/fees/rules'),
  })
  const invalidate = () => {
    qc.invalidateQueries({ queryKey: ['acc-fee-rules'] })
    qc.invalidateQueries({ queryKey: ['acc-summary'] })
  }
  const create = useMutation({
    mutationFn: (d: FeeRuleDraft) => api.post('/accounting/fees/rules', d),
    onSuccess: () => {
      toast.success('Тариф добавлен')
      invalidate()
    },
    onError: (e) => toast.error(e instanceof ApiError ? e.message : 'Не удалось сохранить'),
  })
  const remove = useMutation({
    mutationFn: (id: number) => api.del(`/accounting/fees/rules/${id}`),
    onSuccess: () => {
      toast.success('Тариф удалён')
      invalidate()
    },
    onError: (e) => toast.error(e instanceof ApiError ? e.message : 'Не удалось удалить'),
  })
  return (
    <div className="flex flex-col gap-4">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <DateRange value={range} onChange={setRange} presets={[7, 30, 90, 365]} />
      </div>
      <SectionHeader
        className="mb-0"
        title="По каналам оплаты"
        description="Канал — метод и шлюз, который выставил счёт. Комиссии IDMPay записаны под провайдером СБП, поэтому отдельной строкой видны только здесь."
        actions={
          <Select
            className="w-full sm:w-64"
            label="Канал"
            value={channel}
            options={[
              { value: '', label: 'Все каналы' },
              ...CHANNELS.map((c) => ({ value: c, label: channelLabel(c) })),
            ]}
            onChange={setChannel}
          />
        }
      />
      <ChannelBreakdownTable
        items={summary.isLoading ? undefined : (summary.data?.fees_by_channel ?? [])}
        error={summary.data ? null : summary.error}
        onRetry={() => void summary.refetch()}
      />
      <SectionHeader className="mb-0" title="По провайдерам" />
      {summary.error && !summary.data ? (
        <Card>
          <ErrorState
            title="Комиссии по провайдерам не загрузились"
            error={summary.error}
            retrying={summary.isFetching}
            onRetry={() => void summary.refetch()}
          />
        </Card>
      ) : (
        <FeeBreakdownTable
          items={summary.data?.fees_by_provider}
          payoutFeeRub={summary.data?.payout_fees_rub}
          payoutSources={summary.data?.payout_fees_sources}
        />
      )}
      {Object.keys(summary.data?.refunded_fee_estimates ?? {}).length > 0 && (
        <Card>
          <div className="flex flex-col gap-2 text-body">
            <span className="flex flex-wrap items-center gap-2 text-ink-2">
              <StatusBadge tone="warn">Оценка</StatusBadge>
              Комиссии по возвращённым платежам посчитаны по тарифу: провайдер не подтвердил, удерживает ли он их при
              возврате.
            </span>
            {Object.entries(summary.data?.refunded_fee_estimates ?? {}).map(([ch, v]) => (
              <KV key={ch} k={`${channelLabel(ch)}: оценка по возвращённым`} v={fmtMoney(v, 'RUB')} />
            ))}
          </div>
        </Card>
      )}
      <p className="text-caption text-ink-3">
        «Факт провайдера» — комиссия из ответа провайдера по конкретному платежу (Heleket: commission и convert, СБП:
        запрос транзакции после оплаты, Heleket выплаты: commission). «Котировка» — цифра из ответа при создании счёта
        СБП. «По правилу» — оценка по тарифу ниже, когда провайдер цифру не прислал.
      </p>
      <SectionHeader className="mb-0" title="Тарифы" />
      {rules.error && !rules.data ? (
        <Card>
          <ErrorState
            title="Тарифы комиссий не загрузились"
            error={rules.error}
            retrying={rules.isFetching}
            onRetry={() => void rules.refetch()}
          />
        </Card>
      ) : (
        <FeeRulesTable
          rules={rules.data?.items}
          kinds={
            rules.data?.kinds ?? {
              heleket: ['acquiring', 'conversion'],
              sbp: ['acquiring', 'conversion'],
              idmpay: ['acquiring'],
              wata: ['acquiring'],
              stars: ['stars_withdrawal'],
              heleket_payout: ['payout_network'],
            }
          }
          currencies={rules.data?.currencies ?? ['USDT', 'USD', 'RUB', 'XTR']}
          canEdit={canEdit && (rules.data?.can_edit ?? false)}
          busy={create.isPending || remove.isPending}
          onCreate={(d) => create.mutate(d)}
          onDelete={async (r) => {
            const what = `${FEE_PROVIDER_LABEL[r.provider] ?? r.provider}, ${FEE_KIND_LABEL[r.kind] ?? r.kind}, с ${r.effective_from}`
            if (
              await confirm({
                title: 'Удалить тариф?',
                body: `${what}. Записанные комиссии не изменятся.`,
                danger: true,
              })
            )
              remove.mutate(r.id)
          }}
        />
      )}
    </div>
  )
}

// ───────────────────────────── expenses ─────────────────────────────

interface AdvanceRow {
  id: number
  provider: string
  provider_title: string
  topped_up_on: string
  amount: string
  currency: string
  amount_usd: string
  rate_rub: string
  amount_rub: string
  note: string | null
  receipt_url: string | null
  reverses_id: number | null
  reversed_by_id: number | null
}

interface AdvancesResponse {
  items: AdvanceRow[]
  total: number
  balances: Advances
}

function StornoBadge({ row }: { row: { reverses_id?: number | null; reversed_by_id?: number | null } }) {
  const state = stornoState(row)
  if (state === 'storno') return <StatusBadge tone="neutral">Сторно №{row.reverses_id}</StatusBadge>
  if (state === 'reversed') return <StatusBadge tone="warn">Сторнирован</StatusBadge>
  return null
}

function ReceiptLink({ url }: { url?: string | null }) {
  if (!url) return <span className="text-ink-3">—</span>
  return (
    <a className={LINK_CLASS} href={url} target="_blank" rel="noopener noreferrer">
      Чек
    </a>
  )
}

function ExpensesTab({ canEdit }: { canEdit: boolean }) {
  const qc = useQueryClient()
  const toast = useToast()
  const confirm = useConfirm()
  const { role } = useAuth()
  const [offset, setOffset] = useState(0)
  const [pageSize, setPageSize] = useState(50)
  // Marketing spend names its acquisition channel / campaign (docs/acquisition.md §4); the lists
  // need acquisition.view — every role that may enter expenses has it.
  const canAcq = canEdit && canDo(role, 'acquisition.view')
  const expensesQ = useQuery({
    queryKey: ['acc-expenses', offset, pageSize],
    queryFn: () => api.get<ExpensesResponse>(`/accounting/expenses?limit=${pageSize}&offset=${offset}`),
  })
  const advancesQ = useQuery({
    queryKey: ['acc-advances'],
    queryFn: () => api.get<AdvancesResponse>('/accounting/advances?limit=100'),
  })
  const data = expensesQ.data
  const channels = useQuery({
    queryKey: ['acq', 'channels'],
    queryFn: () => api.get<AcqChannelsResponse>('/acquisition/channels'),
    enabled: canAcq,
  })
  // Every campaign, page by page (review api-ui-8: one request stopped at 1000).
  const campaigns = useQuery(allCampaignsQuery(canAcq))
  const invalidate = () => {
    qc.invalidateQueries({ queryKey: ['acc-expenses'] })
    qc.invalidateQueries({ queryKey: ['acc-advances'] })
    qc.invalidateQueries({ queryKey: ['acc-summary'] })
    qc.invalidateQueries({ queryKey: ['acc-monthly'] })
    qc.invalidateQueries({ queryKey: ['provider-balances'] })
    qc.invalidateQueries({ queryKey: ['dashboard', 'provider-overview'] })
    qc.invalidateQueries({ queryKey: ['acq'] }) // acquisition spend and campaign «spent»
  }
  const add = useMutation({
    mutationFn: (body: Record<string, unknown>) => api.post<{ kind?: string }>('/accounting/expenses', body),
    onSuccess: (r) => {
      toast.success(r.kind === 'advance' ? 'Пополнение записано как аванс провайдеру' : 'Расход записан')
      invalidate()
    },
    onError: (e) => toast.error(e instanceof ApiError ? e.message : 'Не удалось сохранить'),
  })
  const reverse = useMutation({
    mutationFn: ({ id, reason }: { id: number; reason: string }) => api.post(`/accounting/expenses/${id}/reverse`, { reason }),
    onSuccess: () => {
      toast.success('Расход сторнирован')
      invalidate()
    },
    onError: (e) => toast.error(e instanceof ApiError ? e.message : 'Не удалось сторнировать'),
  })
  const edit = useMutation({
    mutationFn: ({ id, amount }: { id: number; amount: string }) => api.patch(`/accounting/expenses/${id}`, { amount }),
    onSuccess: () => {
      toast.success('Исправлено: старая строка сторнирована, новая записана')
      invalidate()
    },
    onError: (e) => toast.error(e instanceof ApiError ? e.message : 'Не удалось исправить'),
  })
  const reverseAdvance = useMutation({
    mutationFn: (id: number) => api.post(`/accounting/advances/${id}/reverse`, {}),
    onSuccess: () => {
      toast.success('Пополнение сторнировано')
      invalidate()
    },
    onError: (e) => toast.error(e instanceof ApiError ? e.message : 'Не удалось сторнировать'),
  })
  async function onReverse(e: ExpenseRow) {
    if (
      await confirm({
        title: 'Сторнировать расход?',
        body: `${rub(e.amount_rub)} ₽ — ${e.note ?? CATEGORY_LABEL[e.category] ?? e.category}. Строка останется, рядом появится сторно с обратной суммой. Если месяц закрыт, сторно встанет в текущий месяц.`,
        confirmLabel: 'Сторнировать',
        danger: true,
      })
    )
      reverse.mutate({ id: e.id, reason: '' })
  }
  function onEdit(e: ExpenseRow) {
    const amount = window.prompt(`Новая сумма в ${e.currency} вместо ${e.amount}. Старая строка будет сторнирована:`, e.amount)
    if (amount !== null && amount.trim()) edit.mutate({ id: e.id, amount: amount.replace(/\s/g, '').replace(',', '.') })
  }
  const busy = reverse.isPending || edit.isPending
  const expenseColumns: DataColumn<ExpenseRow>[] = [
    { id: 'date', header: 'Дата', type: 'date', cell: (e) => fmtDate(e.spent_on), mobile: 'meta' },
    {
      id: 'category',
      header: 'Категория',
      type: 'name',
      cell: (e) => (
        <span className="inline-flex flex-wrap items-center gap-2">
          {CATEGORY_LABEL[e.category] ?? e.category}
          <StornoBadge row={e} />
        </span>
      ),
      mobile: 'title',
    },
    {
      id: 'source',
      header: 'Привлечение',
      mobile: 'hidden',
      cell: (e) => (
        <span className="text-caption">
          {expenseSource(e) ?? (e.category === 'marketing' ? <span className="text-ink-3">не распределено</span> : '—')}
        </span>
      ),
    },
    {
      id: 'amount',
      header: 'Сумма',
      type: 'money',
      className: 'text-ink-2',
      mobile: 'hidden',
      cell: (e) => (
        <span className="inline-flex flex-col items-end">
          {fmtMoney(e.amount, e.currency)}
          {e.currency !== 'RUB' && <span className="text-caption text-ink-3">курс {fmtRate(e.rate_rub)}</span>}
        </span>
      ),
    },
    { id: 'rub', header: 'В рублях', type: 'money', unit: '₽', cell: (e) => fmtMoney(e.amount_rub), mobile: 'amount' },
    { id: 'note', header: 'Комментарий', cell: (e) => e.note ?? '—', mobile: 'meta' },
    { id: 'receipt', header: 'Чек', mobile: 'hidden', cell: (e) => <ReceiptLink url={e.receipt_url} /> },
    ...(canEdit
      ? [
          {
            id: 'actions',
            header: '',
            type: 'actions' as const,
            cell: (e: ExpenseRow) =>
              stornoState(e) === null ? (
                <RowMenu
                  label={`Действия с расходом ${fmtDate(e.spent_on)}`}
                  items={[
                    { id: 'edit', label: 'Исправить сумму', disabled: busy, onSelect: () => onEdit(e) },
                    { id: 'reverse', label: 'Сторнировать', danger: true, disabled: busy, onSelect: () => void onReverse(e) },
                  ]}
                />
              ) : null,
          },
        ]
      : []),
  ]
  const advanceColumns: DataColumn<AdvanceRow>[] = [
    { id: 'date', header: 'Дата', type: 'date', mobile: 'meta', cell: (a) => fmtDate(a.topped_up_on) },
    {
      id: 'provider',
      header: 'Провайдер',
      type: 'name',
      mobile: 'title',
      cell: (a) => (
        <span className="inline-flex flex-wrap items-center gap-2">
          {a.provider_title}
          <StornoBadge row={a} />
        </span>
      ),
    },
    { id: 'amount', header: 'Сумма', type: 'money', className: 'text-ink-2', mobile: 'hidden', cell: (a) => fmtMoney(a.amount, a.currency) },
    { id: 'usd', header: 'В долларах', type: 'money', unit: '$', mobile: 'hidden', cell: (a) => fmtMoney(a.amount_usd) },
    { id: 'rub', header: 'В рублях', type: 'money', unit: '₽', mobile: 'amount', cell: (a) => fmtMoney(a.amount_rub) },
    { id: 'note', header: 'Комментарий', mobile: 'meta', cell: (a) => a.note ?? '—' },
    { id: 'receipt', header: 'Чек', mobile: 'hidden', cell: (a) => <ReceiptLink url={a.receipt_url} /> },
    ...(canEdit
      ? [
          {
            id: 'actions',
            header: '',
            type: 'actions' as const,
            cell: (a: AdvanceRow) =>
              stornoState(a) === null ? (
                <RowMenu
                  label={`Действия с пополнением ${fmtDate(a.topped_up_on)}`}
                  items={[
                    {
                      id: 'reverse',
                      label: 'Сторнировать',
                      danger: true,
                      disabled: reverseAdvance.isPending,
                      onSelect: async () => {
                        if (
                          await confirm({
                            title: 'Сторнировать пополнение?',
                            body: `${a.provider_title}: ${fmtUsdt(a.amount_usd).replace('USDT', '$')}. Аванс провайдеру уменьшится.`,
                            confirmLabel: 'Сторнировать',
                            danger: true,
                          })
                        )
                          reverseAdvance.mutate(a.id)
                      },
                    },
                  ]}
                />
              ) : null,
          },
        ]
      : []),
  ]

  return (
    <div className="flex flex-col gap-4">
      {canEdit && (
        <div id="expense-form">
          <ExpenseForm
            today={todayIso()}
            categories={data?.categories ?? Object.keys(CATEGORY_LABEL)}
            categoryLabel={CATEGORY_LABEL}
            currencies={data?.currencies ?? ['RUB', 'USDT', 'USD', 'EUR']}
            meta={expenseMeta(data)}
            channels={canAcq ? channels.data?.items : undefined}
            campaigns={canAcq ? campaigns.data?.items : undefined}
            providers={data?.advance_providers}
            busy={add.isPending}
            onSubmit={(body) =>
              add.mutateAsync(body).then(
                () => true,
                () => false,
              )
            }
          />
        </div>
      )}
      <QueryTable
        label="Расходы"
        scope="expenses"
        query={expensesQ}
        rows={(d) => d.items}
        columns={expenseColumns}
        getRowId={(e) => String(e.id)}
        errorTitle="Не удалось загрузить расходы"
        empty={
          <EmptyState
            title="Расходов за год нет"
            text="Хостинг, реклама, зарплаты и налоги вычитаются из прибыли периода."
            action={
              canEdit
                ? {
                    label: 'Добавить расход',
                    onClick: () =>
                      document.querySelector<HTMLElement>('#expense-form input, #expense-form select')?.focus(),
                  }
                : undefined
            }
          />
        }
      />
      <Pager
        offset={offset}
        pageSize={pageSize}
        total={data?.total ?? 0}
        busy={expensesQ.isFetching}
        onOffset={setOffset}
        onPageSize={(n) => {
          setPageSize(n)
          setOffset(0)
        }}
      />
      <section>
        <SectionHeader
          as="h3"
          title="Пополнения провайдеров"
          description="Аванс провайдеру, а не расход: в прибыль идёт только себестоимость генераций. Остаток аванса — в «Сводке»."
        />
        {advancesQ.error && !advancesQ.data ? (
          <Card>
            <ErrorState title="Не удалось загрузить пополнения" error={advancesQ.error} onRetry={() => void advancesQ.refetch()} />
          </Card>
        ) : (advancesQ.data?.items.length ?? 0) === 0 ? (
          <EmptyState title="Пополнений нет" text="Внесите пополнение формой выше, категория «Пополнение провайдеров»." />
        ) : (
          <DataTable label="Пополнения провайдеров" rows={advancesQ.data?.items ?? []} columns={advanceColumns} getRowId={(a) => String(a.id)} />
        )}
      </section>
    </div>
  )
}

// ───────────────────────────── partners ─────────────────────────────

interface Partner {
  id: number
  name: string
  role_title: string | null
  share_pct: string
  payout_details: string | null
  note: string | null
  is_active: boolean
}

function PartnersTab({ canEdit }: { canEdit: boolean }) {
  const qc = useQueryClient()
  const toast = useToast()
  const confirm = useConfirm()
  const [form, setForm] = useState({ name: '', role_title: '', share_pct: '', payout_details: '', note: '' })
  const [draft, setDraft] = useState<Record<number, Partial<Partner>>>({})
  const partnersQ = useQuery({
    queryKey: ['acc-partners'],
    queryFn: () =>
      api.get<{ items: Partner[]; active_share_total: string; details_revealed: boolean }>('/accounting/partners'),
  })
  const data = partnersQ.data
  const invalidate = () => qc.invalidateQueries({ queryKey: ['acc-partners'] })
  const create = useMutation({
    mutationFn: () => api.post('/accounting/partners', { ...form, share_pct: form.share_pct.replace(',', '.') }),
    onSuccess: () => {
      toast.success('Участник добавлен')
      setForm({ name: '', role_title: '', share_pct: '', payout_details: '', note: '' })
      invalidate()
    },
    onError: (e) => toast.error(e instanceof ApiError ? e.message : 'Не удалось сохранить'),
  })
  const patch = useMutation({
    mutationFn: ({ id, body }: { id: number; body: Partial<Partner> }) => api.patch(`/accounting/partners/${id}`, body),
    onSuccess: (_r, v) => {
      toast.success('Сохранено')
      setDraft((s) => {
        const n = { ...s }
        delete n[v.id]
        return n
      })
      invalidate()
    },
    onError: (e) => toast.error(e instanceof ApiError ? e.message : 'Не удалось сохранить'),
  })
  const remove = useMutation({
    mutationFn: (id: number) => api.del(`/accounting/partners/${id}`),
    onSuccess: () => {
      toast.success('Участник удалён')
      invalidate()
    },
    onError: () => toast.error('Не удалось удалить'),
  })
  const total = data?.active_share_total ?? '0'
  const left = subDecimal('100', total, 6)
  async function onRemove(p: Partner) {
    if (
      await confirm({
        title: 'Удалить участника?',
        body: `${p.name}: участник с прошлыми распределениями будет деактивирован, без них — удалён.`,
        confirmLabel: 'Удалить',
        danger: true,
      })
    )
      remove.mutate(p.id)
  }
  async function onToggle(p: Partner) {
    if (p.is_active) {
      const ok = await confirm({
        title: `Выключить участника ${p.name}?`,
        body: 'Он не получит долю в следующих распределениях. Прошлые распределения не меняются.',
        confirmLabel: 'Выключить',
        danger: true,
      })
      if (!ok) return
    }
    patch.mutate({ id: p.id, body: { is_active: !p.is_active } })
  }
  const partnerColumns: DataColumn<Partner>[] = [
    {
      id: 'name',
      header: 'Участник',
      type: 'name',
      mobile: 'title',
      cell: (p) => (
        <span className="flex flex-col">
          <span className="text-ink-1">{p.name}</span>
          {p.note && <span className="text-caption text-ink-3">{p.note}</span>}
        </span>
      ),
    },
    { id: 'role', header: 'Роль', cell: (p) => p.role_title ?? '—', mobile: 'meta' },
    {
      id: 'share',
      header: 'Доля, %',
      type: 'percent',
      mobile: canEdit ? 'field' : 'amount',
      cell: (p) => {
        const cur = draft[p.id] ?? {}
        return canEdit ? (
          <Input
            size="sm"
            aria-label={`Доля, %, ${p.name}`}
            inputMode="decimal"
            className="tnum h-9 w-24 text-right"
            value={cur.share_pct ?? p.share_pct}
            onChange={(e) => setDraft((s) => ({ ...s, [p.id]: { ...s[p.id], share_pct: e.target.value } }))}
          />
        ) : (
          fmtPct(p.share_pct, { digits: 0, max: 3 })
        )
      },
    },
    {
      id: 'details',
      header: 'Реквизиты',
      mobile: 'hidden',
      cell: (p) => (
        <span className="tnum text-caption text-ink-2" data-raw>
          {p.payout_details ?? '—'}
        </span>
      ),
    },
    {
      id: 'status',
      header: 'Статус',
      type: 'status',
      mobile: 'status',
      cell: (p) => (p.is_active ? <StatusBadge tone="ok">Активен</StatusBadge> : <StatusBadge tone="neutral">Неактивен</StatusBadge>),
    },
    ...(canEdit
      ? [
          {
            id: 'actions',
            header: '',
            type: 'actions' as const,
            cell: (p: Partner) => {
              const cur = draft[p.id] ?? {}
              const dirty = Object.keys(cur).length > 0
              return (
                <span className="inline-flex items-center gap-2">
                  <Button
                    size="sm"
                    variant="secondary"
                    disabled={!dirty}
                    onClick={() => patch.mutate({ id: p.id, body: { share_pct: String(cur.share_pct ?? p.share_pct).replace(',', '.') } })}
                  >
                    Сохранить
                  </Button>
                  <RowMenu
                    label={`Действия с участником ${p.name}`}
                    items={[
                      { id: 'toggle', label: p.is_active ? 'Выключить' : 'Включить', danger: p.is_active, onSelect: () => void onToggle(p) },
                      { id: 'remove', label: 'Удалить', danger: true, disabled: remove.isPending, onSelect: () => void onRemove(p) },
                    ]}
                  />
                </span>
              )
            },
          },
        ]
      : []),
  ]

  return (
    <div className="flex flex-col gap-4">
      <Card>
        <div className="flex flex-wrap items-center justify-between gap-3">
          <span className="text-body text-ink-2">
            Сумма активных долей: <span className="tnum text-ink-1">{fmtPct(total, { digits: 3 })}</span>
            {!left.startsWith('-') && /[1-9]/.test(left) && (
              <span className="text-caption text-ink-3">
                {' '}
                (в компании остаётся <span className="tnum">{fmtPct(left, { digits: 3 })}</span>)
              </span>
            )}
          </span>
          {!canEdit && <StatusBadge tone="neutral">Изменять может только владелец</StatusBadge>}
          {data && !data.details_revealed && (
            <StatusBadge tone="neutral">Реквизиты скрыты, видны последние 4 символа</StatusBadge>
          )}
        </div>
      </Card>
      {canEdit && (
        <Card id="partner-form">
          <div className="grid grid-cols-2 gap-3 md:grid-cols-6">
            <div>
              <Label htmlFor="accounting-f1">Имя</Label>
              <Input
                id="accounting-f1"
                value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
                placeholder="Иван Петров"
              />
            </div>
            <div>
              <Label htmlFor="accounting-f2">Роль</Label>
              <Input
                id="accounting-f2"
                value={form.role_title}
                onChange={(e) => setForm({ ...form, role_title: e.target.value })}
                placeholder="основатель / инвестор"
              />
            </div>
            <div>
              <Label htmlFor="accounting-f3">Доля, %</Label>
              <Input
                id="accounting-f3"
                inputMode="decimal"
                value={form.share_pct}
                onChange={(e) => setForm({ ...form, share_pct: e.target.value })}
                placeholder="0"
              />
            </div>
            <div>
              <Label htmlFor="accounting-f4">Реквизиты</Label>
              <Input
                id="accounting-f4"
                value={form.payout_details}
                onChange={(e) => setForm({ ...form, payout_details: e.target.value })}
                placeholder="USDT TRC20 / банк"
              />
            </div>
            <div>
              <Label htmlFor="accounting-f5">Комментарий</Label>
              <Input
                id="accounting-f5" value={form.note} onChange={(e) => setForm({ ...form, note: e.target.value })} />
            </div>
            <div className="flex items-end">
              <Button
                variant="primary"
                loading={create.isPending}
                disabled={!form.name || !form.share_pct}
                onClick={() => create.mutate()}
              >
                Добавить
              </Button>
            </div>
          </div>
        </Card>
      )}
      <QueryTable
        label="Участники и доли"
        query={partnersQ}
        rows={(pd) => pd.items}
        columns={partnerColumns}
        getRowId={(p) => String(p.id)}
        errorTitle="Не удалось загрузить участников"
        empty={
          <EmptyState
            title="Участников пока нет"
            text="Участник получает долю прибыли при каждом распределении."
            action={
              canEdit
                ? {
                    label: 'Добавить участника',
                    onClick: () => document.querySelector<HTMLInputElement>('#partner-form input')?.focus(),
                  }
                : undefined
            }
          />
        }
      />
    </div>
  )
}

// ───────────────────────────── distributions ─────────────────────────────

function DistributionsTab({ canEdit }: { canEdit: boolean }) {
  const qc = useQueryClient()
  const toast = useToast()
  const confirm = useConfirm()
  const [range, setRange] = useState<Range>({ from: monthStart(), to: todayIso() })
  const [note, setNote] = useState('')
  const distQ = useQuery({
    queryKey: ['acc-distributions'],
    queryFn: () => api.get<{ items: Distribution[] }>('/accounting/distributions'),
  })
  const data = distQ.data
  const invalidate = () => qc.invalidateQueries({ queryKey: ['acc-distributions'] })
  const create = useMutation({
    mutationFn: () => api.post('/accounting/distributions', { period_start: range.from, period_end: range.to, note }),
    onSuccess: () => {
      toast.success('Распределение рассчитано (черновик)')
      setNote('')
      invalidate()
    },
    onError: (e) => toast.error(e instanceof ApiError ? e.message : 'Не удалось рассчитать'),
  })
  const approve = useMutation({
    mutationFn: (id: number) => api.post(`/accounting/distributions/${id}/approve`),
    onSuccess: () => {
      toast.success('Утверждено')
      invalidate()
    },
    onError: (e) => toast.error(e instanceof ApiError ? e.message : 'Не удалось утвердить'),
  })
  const remove = useMutation({
    mutationFn: (id: number) => api.del(`/accounting/distributions/${id}`),
    onSuccess: () => {
      toast.success('Черновик удалён')
      invalidate()
    },
    onError: (e) => toast.error(e instanceof ApiError ? e.message : 'Не удалось удалить'),
  })
  const paid = useMutation({
    mutationFn: ({ id, ref }: { id: number; ref: string }) =>
      api.post(`/accounting/distributions/shares/${id}/paid`, { paid_ref: ref }),
    onSuccess: () => {
      toast.success('Выплата отмечена')
      invalidate()
    },
    onError: (e) => toast.error(e instanceof ApiError ? e.message : 'Не удалось отметить'),
  })
  const busy = approve.isPending || remove.isPending || paid.isPending
  return (
    <div className="flex flex-col gap-4">
      {canEdit && (
        <Card>
          <div className="flex flex-wrap items-end gap-3">
            <DateRange label="Период" value={range} onChange={setRange} presets={[30, 90]} />
            <div className="min-w-64 flex-1">
              <Label htmlFor="accounting-f6">Комментарий</Label>
              <Input
                id="accounting-f6" value={note} onChange={(e) => setNote(e.target.value)} placeholder="например: сентябрь 2026" />
            </div>
            <Button
              variant="primary"
              loading={create.isPending}
              disabled={!range.from || !range.to}
              onClick={() => create.mutate()}
            >
              Рассчитать
            </Button>
          </div>
          <p className="mt-2 text-caption text-ink-3">
            Расчёт замораживает выручку, себестоимость, выплаты, расходы и курс за период и делит прибыль по текущим
            активным долям. Черновик можно удалить; утверждённое распределение — запись, по которой отмечаются выплаты.
          </p>
        </Card>
      )}
      {distQ.error && !data ? (
        <Card>
          <ErrorState
            title="Не удалось загрузить распределения"
            error={distQ.error}
            retrying={distQ.isFetching}
            onRetry={() => void distQ.refetch()}
          />
        </Card>
      ) : (
        <DistributionTable
          items={data?.items}
          canEdit={canEdit}
          busy={busy}
          onApprove={async (id) => {
            if (
              await confirm({
                title: 'Утвердить распределение?',
                body: 'После утверждения доли зафиксированы и черновик нельзя удалить.',
              })
            )
              approve.mutate(id)
          }}
          onDelete={async (id) => {
            if (await confirm({ title: 'Удалить черновик?', body: 'Расчёт можно повторить позже.' })) remove.mutate(id)
          }}
          onMarkPaid={(s: DistributionShare) => {
            const ref = window.prompt(
              `Выплата ${rub(s.amount_rub)} ₽ участнику ${s.partner_name}. Референс перевода (tx hash, номер):`,
              '',
            )
            if (ref !== null) paid.mutate({ id: s.id, ref })
          }}
        />
      )}
    </div>
  )
}
